import * as Screen from "./lib/Screen.ts";
import * as FwDatas from "./FwDatas.ts";
import { TaskCategoryCache } from "./server/objects/TaskBeans.ts";
import { changedFieldNames } from "./util/formChanges.ts";

/**
 * Segment track events of the Web tracking plan.
 * Keep in sync with the Segment "Web" tracking plan: a name missing from the plan is blocked.
 */
export type WebEventName =
  // Viewed
  | "Page Viewed" | "Launchpad Viewed"
  // Settings
  | "Setting Notification Toggled" | "Setting Password Changed"
  // Family & circle
  | "Family Member Removed" | "Family Member Role Changed" | "Family Profile Edited"
  | "Family Storage Viewed" | "Family Switched"
  | "Circle Created" | "Circle Member Invited" | "Circle Invitation Accepted"
  // Premium & paywall
  | "Premium Screen Viewed" | "Premium Screen Dismissed" | "Premium Plan Selected"
  | "Premium Upgrade Tapped" | "Premium FAQ Viewed" | "Premium Legal Link Tapped"
  | "Premium Promo Offer Viewed" | "Premium Promo Offer Tapped"
  | "Premium Subscription Manage Tapped" | "Premium Support Tapped"
  // Tasks & lists
  | "Task Created" | "Task Assigned" | "Task Category Changed" | "Task Completed"
  | "Task Due Date Set" | "Task List Changed" | "Task List Cleanup Tapped" | "Task Photo Added"
  | "Task Recurrence Set" | "Task Reminder Set" | "List Created"
  // Budget
  | "Budget Setup Completed" | "Budget Currency Changed" | "Budget Period Changed"
  | "Budget Category Created" | "Budget Transaction Created" | "Budget Transaction Edited"
  | "Budget Transaction Deleted" | "Budget Transaction Viewed" | "Budget Export Tapped"
  // Calendar & contacts
  | "Calendar Viewed" | "Calendar Connected" | "Calendar Event Deleted" | "Calendar Event Edited"
  | "Contact Created" | "Contact Edited" | "Contact Deleted" | "Contact Viewed"
  // Files & photos
  | "File Uploaded" | "File Viewed" | "File Deleted" | "File Moved" | "File Renamed"
  | "File Shared" | "Folder Created"
  | "Photo Viewed" | "Photo Deleted" | "Photo Downloaded"
  // Meal planner & recipes
  | "Recipe Saved" | "Recipe Direction Edited" | "Recipe Ingredient Edited" | "Recipe Photo Added"
  | "Recipe Servings Set" | "Recipe Time Set" | "Meal Exported To Tasks"
  // Location
  | "Location Request Sent" | "Place Created" | "Place Edited" | "Place Deleted"
  // Conversations
  | "Conversation Opened" | "Conversation Created" | "Message Sent" | "Message Media Attached";

/**
 * The web interface the user is on, as the tracking plan expects it in `platform`.
 * Uses the same breakpoint the app renders its mobile layout with (lib/Screen.ts),
 * so the value always matches the interface the user actually saw.
 */
export function getPlatform(): "web_desktop" | "web_mobile" {
  return Screen.isMobileView() ? "web_mobile" : "web_desktop";
}

/**
 * Sends a Segment track event. `platform` is added automatically.
 * Safe to call when Segment is not loaded (OneTrust consent refused, ad blocker).
 *
 * @param event - event name, as declared in the Web tracking plan
 * @param properties - event properties, snake_case keys
 */
export function track(event: WebEventName, properties: Record<string, unknown> = {}): void {
  window.analytics?.track(event, {...properties, platform: getPlatform()});
}

/**
 * Lowercases an internal enum value for an event property (EXPENSE -> expense).
 */
export function enumValue(value: string): string {
  return value ? value.toLowerCase() : undefined;
}

type PageInfo = {page_name: string, section: string};

// raw php page name -> logical page name and product section
// home.php, wall.php and dashboard.php render the same screen, so they share one page name
const PAGE_NAMES: Record<string, PageInfo> = {
  "home.php": {page_name: "home", section: "home"},
  "wall.php": {page_name: "home", section: "home"},
  "dashboard.php": {page_name: "home", section: "home"},
  "events.php": {page_name: "calendar", section: "calendar"},
  "outlookreturn.php": {page_name: "calendar", section: "calendar"},
  "specialdays.php": {page_name: "special_days", section: "calendar"},
  "timetable.php": {page_name: "timetable", section: "calendar"},
  "timetable_nc.php": {page_name: "timetable", section: "calendar"},
  "tasks.php": {page_name: "tasks", section: "tasks"},
  "mealplanner.php": {page_name: "meal_planner", section: "meal_planner"},
  "mealplanner_nc.php": {page_name: "meal_planner", section: "meal_planner"},
  "recipebox.php": {page_name: "recipe_box", section: "meal_planner"},
  "recipebox_nc.php": {page_name: "recipe_box", section: "meal_planner"},
  "budget.php": {page_name: "budget", section: "budget"},
  "document.php": {page_name: "files", section: "files"},
  "gallery.php": {page_name: "gallery", section: "photos"},
  "photo_albums.php": {page_name: "gallery", section: "photos"},
  "photos.php": {page_name: "gallery", section: "photos"},
  "contacts.php": {page_name: "contacts", section: "contacts"},
  "im.php": {page_name: "messages", section: "messages"},
  "map.php": {page_name: "map", section: "location"},
  "geohistory.php": {page_name: "map", section: "location"},
  "settings.php": {page_name: "settings", section: "settings"},
  "change_password.php": {page_name: "change_password", section: "settings"},
  "premium.php": {page_name: "premium", section: "premium"},
  "voucher.php": {page_name: "voucher", section: "premium"},
  "fw2_promocode.php": {page_name: "promo_code", section: "premium"},
  "fw2_signin.php": {page_name: "sign_in", section: "authentication"},
  "fw2_signup.php": {page_name: "sign_up", section: "authentication"},
  "fw2_signup_family.php": {page_name: "sign_up_family", section: "authentication"},
  "password_forgotten.php": {page_name: "password_forgotten", section: "authentication"},
  "password_reset.php": {page_name: "password_reset", section: "authentication"},
  "password_reset_success.php": {page_name: "password_reset_success", section: "authentication"},
  "email_validate.php": {page_name: "email_validate", section: "authentication"},
  "termsrecover.php": {page_name: "terms", section: "authentication"},
  "fw2_invitation.php": {page_name: "invitation", section: "invitation"},
  "fw2_invitation_profile.php": {page_name: "invitation_profile", section: "invitation"},
  "fw2_invitation_success.php": {page_name: "invitation_success", section: "invitation"},
  "fw2_invitation_error.php": {page_name: "invitation_error", section: "invitation"},
  "fw2_blockinvitation.php": {page_name: "block_invitation", section: "invitation"},
  "fw2_blockinvitation_success.php": {page_name: "block_invitation_success", section: "invitation"},
  "invitation_already_accepted.php": {page_name: "invitation_already_accepted", section: "invitation"},
};

/**
 * Logical page name and section for a raw php page name.
 * Unknown pages fall back to the file name without its extension.
 */
export function getPageInfo(rawPageName: string): PageInfo {
  const known = PAGE_NAMES[rawPageName];
  if(known) return known;
  const name = (rawPageName || "unknown").replace(/\.php$/, "");
  return {page_name: name, section: name.startsWith("homehub") ? "home_hub" : "other"};
}

/**
 * Sends "Page Viewed" for the current page.
 */
export function pageViewed(rawPageName: string): void {
  track("Page Viewed", getPageInfo(rawPageName));
}

/*
 * Form trackers. Each one reads the form before the API call (the modal closes and the form
 * is reset right after it) and returns the callback that sends the events once the call succeeded:
 *
 *   const tracked = segment.formTaskTracker(form, metaId);
 *   await ApiServer.taskupdate2(form);
 *   tracked();
 */

/** Sends the events of a form once its API call succeeded. */
export type FormTracked = () => void;

function valueOf(form: HTMLFormElement, name: string): string {
  return (form.elements.namedItem(name) as HTMLInputElement)?.value;
}

/** the changed fields as one `field_changed` property: indexed names (attendee.0.accountId) count as their field */
function fieldChanged(names: string[]): string {
  return Array.from(new Set(names.map(name=>name.split('.')[0]).filter(name=>name!=='metaId'))).join(',');
}

// forms whose reminder the user picked himself: the default reminder added along with a due date
// is set by script, so its change event is not trusted
const reminderPickedByUser = new WeakSet<HTMLFormElement>();
if(typeof document !== "undefined"){
  document.addEventListener("change", (event)=>{
    const target = event.target as Element;
    if(event.isTrusted && target?.closest?.(".fw-reminder-block")){
      const form = target.closest("form");
      if(form) reminderPickedByUser.add(form);
    }
  }, true);
}

/**
 * Task form (widget/form/taskedit): Task Created, and on what the user changed Task List Changed,
 * Task Category Changed, Task Assigned, Task Due Date Set, Task Recurrence Set, Task Reminder Set,
 * Task Photo Added.
 *
 * @param metaId - the task edited, empty for a creation
 */
export function formTaskTracker(form: HTMLFormElement, metaId: string): FormTracked {
  const
    changed = changedFieldNames(form),
    assigneeSelect = form.elements.namedItem('assignee') as HTMLSelectElement,
    dueDate = valueOf(form, 'dueDate'),
    recurrency = valueOf(form, 'recurrency'),
    reminderUnit = valueOf(form, 'reminder.reminderUnit'),
    reminderPicked = reminderPickedByUser.has(form) && !!reminderUnit && valueOf(form, 'reminder.reminderType') !== 'NONE',
    categoryName = TaskCategoryCache.get(valueOf(form, 'taskCategoryId'))?.name,
    //toAll is a hidden input of user-picker, $empty is its "nobody" option
    assigneeCount = valueOf(form, 'toAll') === 'true' ? FwDatas.getFamily()?.members?.length
      : Array.from(assigneeSelect?.selectedOptions || []).filter(option=>option.value !== '$empty').length,
    pictures: any[] = Array.from((form.elements.namedItem('picture') as any)?.customFiles || []),
    photoAdded = pictures.some(file=>file instanceof File && file.type?.startsWith('image/'));

  return ()=>{
    if(!metaId) track("Task Created");
    if(metaId && changed.includes('taskListId')) track("Task List Changed");
    if(metaId && changed.includes('taskCategoryId')) track("Task Category Changed",{category_name:categoryName});
    if((changed.includes('assignee') || changed.includes('toAll')) && assigneeCount > 0){
      track("Task Assigned",{assignee_count:assigneeCount});
    }
    if(changed.includes('dueDate') && dueDate && dueDate !== '$empty') track("Task Due Date Set");
    if(changed.includes('recurrency') && recurrency && recurrency !== 'NONE'){
      track("Task Recurrence Set",{frequency_type:enumValue(recurrency)});
    }
    if(reminderPicked) track("Task Reminder Set",{reminder_type:enumValue(reminderUnit)});
    if(photoAdded) track("Task Photo Added");
  };
}

/**
 * Calendar event edit form (calendar_modal): Calendar Event Edited, start and end reported as
 * "date" like a drag and drop does.
 */
export function formEventTracker(form: HTMLFormElement): FormTracked {
  const changed = changedFieldNames(form).map(name=>name==='startDate'||name==='endDate'?'date':name);
  return ()=>track("Calendar Event Edited",{field_changed:fieldChanged(changed)});
}

/**
 * Recipe form (recipe box): Recipe Saved for a creation, Recipe Direction Edited and Recipe
 * Ingredient Edited on an edit, then Recipe Servings Set, Recipe Time Set, Recipe Photo Added.
 * Call it after the hidden recipe.prepTime/cookTime are computed: the change is detected on the
 * visible hour and minute fields, the minutes are read from the hidden ones.
 */
export function formRecipeTracker(form: HTMLFormElement): FormTracked {
  const
    changed = changedFieldNames(form),
    isNew = !valueOf(form, 'recipe.metaId'),
    serves = parseInt(valueOf(form, 'recipe.serves')),
    cookMinutes = parseInt(valueOf(form, 'recipe.cookTime'))||0,
    prepMinutes = parseInt(valueOf(form, 'recipe.prepTime'))||0,
    ingredientCount = String(valueOf(form, 'recipe.ingredients')||'').split('\n').filter(line=>line.trim()).length,
    photoAdded = Array.from(form.querySelectorAll('input[name=picture]')).some((input: any)=>!!input.file);

  return ()=>{
    if(isNew){
      track("Recipe Saved");
    }else{
      if(changed.includes('recipe.instructions')) track("Recipe Direction Edited");
      if(changed.includes('recipe.ingredients')) track("Recipe Ingredient Edited",{ingredient_count:ingredientCount});
    }
    if(changed.includes('recipe.serves') && serves > 0) track("Recipe Servings Set",{serving_count:serves});
    if(['prepHour','prepMin','cookHour','cookMin'].some(name=>changed.includes(name))){
      track("Recipe Time Set",{cook_minutes:cookMinutes,prep_minutes:prepMinutes});
    }
    if(photoAdded) track("Recipe Photo Added");
  };
}

/**
 * Budget transaction form: Budget Transaction Edited. A creation sends nothing here, it is
 * tracked by the shared success callback of page_budget.ts.
 *
 * @param metaId - the transaction edited, empty for a creation
 */
export function formBudgetTransactionTracker(form: HTMLFormElement, metaId: string): FormTracked {
  const changed = changedFieldNames(form);
  return ()=>{
    if(metaId) track("Budget Transaction Edited",{field_changed:fieldChanged(changed)});
  };
}
